<template>
  <div class="TaskPanel create-task-panel" data-test="create-task-panel">
    <section class="task-panel-section">
      <div class="details-header">
        <div>
          <h4 class="details-title">Create Task</h4>
          <div class="details-subtitle">for <strong>{{ patientLabel }}</strong></div>
        </div>
        <div class="details-header-actions">
          <button type="button" class="btn btn-outline-secondary btn-sm" data-test="create-task-cancel" @click="closePanel">Cancel</button>
        </div>
      </div>
      <div class="card panel task-details-main">
        <div class="task-details-main-item">
          <label class="detail-label-block">Task</label>
          <p class="hint">Type the task. Use <code>@</code> to assign it, <code>#</code> to add another patient, and plain-language dates like "by Friday". This patient is already attached.</p>
          <TaskComposer
            :seed-patient="patient"
            :enforce-patient="true"
            :available-tags="availableTags"
            :dropdowns-in-body="false"
            layout="stacked"
            autofocus
            @saved="onSaved"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import TaskComposer from '@/components/TaskComposer/TaskComposer.vue';
import { patientDisplayName } from '@/utils/taskPatients';

/**
 * The Patient Profile "Create Task" slide-out. A thin shell around the shared
 * TaskComposer (extracted from ToDo.vue): it seeds the composer with THIS patient
 * and enforces the patient tag, then closes the panel on save. Opened via
 * $showPanel from PatientProfile.vue::onCreateTask — deliberately NOT role-gated
 * ("anyone should be able to create a task" — Saumil).
 */
export default {
  name: 'CreateTaskPanel',
  components: { TaskComposer },
  props: {
    siteID: { type: String, required: true },
    patient: { type: Object, required: true },
  },
  computed: {
    patientLabel() {
      return patientDisplayName(this.patient) || ('Patient ' + this.patient.PatNum);
    },
    availableTags() {
      return this.$store.state.tasks.siteTaskTags || [];
    },
  },
  methods: {
    onSaved(taskId) {
      this.$toast.success('Task created');
      this.$emit('closePanel', { action: 'saved', taskId });
    },
    closePanel() {
      this.$emit('closePanel', { action: 'cancel' });
    },
  },
};
</script>

<style lang="scss" scoped>
/* Mirrors Kanban/AddTaskPanel.vue so the panel feels like the other task panels; shared partial is a follow-up */
.TaskPanel {
  font-family: 'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif;
  color: #334155;
  background-color: #f8fafc;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.task-panel-section {
  display: flex;
  flex-direction: column;
  padding: 1rem;
  flex: 1;
}

.details-header {
  margin-bottom: 0.875rem;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.details-header-actions {
  display: flex;
  align-items: center;
}

.details-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
}

.task-details-main {
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  margin-bottom: 1rem;
  padding: 1rem;
}

.task-details-main-item {
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.detail-label-block {
  display: block;
  font-weight: 600;
  color: #475569;
  font-size: 0.875rem;
  margin-bottom: 0.375rem;
}

.details-subtitle { color: #64748b; font-size: .875rem; }
.hint { color: #64748b; font-size: .8125rem; margin-bottom: .5rem; }
</style>
